import { iconArrowTop, iconClose } from "assets/icons";
import { InputFormField } from "components/InputFormField";
import { CheckBox } from "components/CheckBox";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import React, {
  ChangeEvent,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  ErrorSpan,
  Label,
  Select,
  SelectHeader,
  SelectOptionsContainer,
  SelectOptionsList,
  SelectedTagsContainer,
  Tag,
  TagRemoveButton,
} from "./styles";

type SizeBtnProps = "small" | "large";
type AppearWhereProps = "bottom" | "top";

export interface MultiSelectProps {
  name: string;
  label: string;
  options: OptionProps[];
  readOnly?: boolean;
  appearWhere?: AppearWhereProps;
  size?: SizeBtnProps;
  block?: boolean;
  value?: string[];
  error?: boolean;
  errorMessage?: string;
  search?: boolean;
  searching?: boolean;
  onChange?: (selectedValues: string[]) => void;
  onSearchInputChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

interface OptionProps {
  value: string;
  label: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = React.memo(
  ({
    name,
    label,
    options,
    readOnly = false,
    appearWhere = "bottom",
    size = "large",
    block = false,
    value = [],
    error,
    errorMessage,
    search,
    searching,
    onChange,
    onSearchInputChange,
  }) => {
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [selectedValues, setSelectedValues] = useState<string[]>(value);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const selectRef = useRef<any>(null);

    // Sync internal state with external value prop
    useEffect(() => {
      setSelectedValues(value);
    }, [value]);

    useOnClickOutside(selectRef, () => {
      setShowDropdown(false);
      setSearchTerm(""); // Clear search when closing dropdown
    });

    const handleOptionSelected = useCallback(
      (event: any) => {
        const { checked, value: eventValue } = event;
        setSelectedValues((prev) => {
          const newSelectedValues = checked
            ? [...prev, eventValue]
            : prev.filter((v) => v !== eventValue);
          onChange?.(newSelectedValues);
          return newSelectedValues;
        });
      },
      [onChange]
    );

    const handleRemoveTag = useCallback(
      (valueToRemove: string) => {
        setSelectedValues((prev) => {
          const newSelectedValues = prev.filter((v) => v !== valueToRemove);
          onChange?.(newSelectedValues);
          return newSelectedValues;
        });
      },
      [onChange]
    );

    const handleShowDropdown = useCallback(() => {
      if (readOnly) return;
      setShowDropdown(!showDropdown);
    }, [readOnly, showDropdown]);

    const handleSearchChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        onSearchInputChange?.(e);
      },
      [onSearchInputChange]
    );

    const filteredAndSortedOptions = useMemo(() => {
      let filtered = options;

      // Filter by search term if present
      if (searchTerm.trim()) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        filtered = options.filter((option) =>
          option.label.toLowerCase().includes(lowerSearchTerm)
        );
      }

      // Sort alphabetically by label (case-insensitive)
      return [...filtered].sort((a, b) => {
        const labelA = a.label.toLowerCase().trim();
        const labelB = b.label.toLowerCase().trim();
        return labelA.localeCompare(labelB, 'pt-BR');
      });
    }, [options, searchTerm]);

    const selectedLabels = useMemo(() => {
      return selectedValues.map(
        (value) =>
          options.find((option) => option.value === value)?.label || value
      );
    }, [selectedValues, options]);

    return (
      <div>
        {selectedValues.length > 0 && <Label>{label}</Label>}
        <Select className={"multi-select"} block={block} ref={selectRef}>
          <SelectHeader
            block={block}
            size={size}
            readOnly={readOnly}
            appearWhere={appearWhere}
            showDropdown={showDropdown}
            touched={selectedValues.length > 0}
            onClick={handleShowDropdown}
            selected={selectedValues.length > 0}
            error={error}
          >
            <div className="select">
              {selectedValues.length > 0 ? (
                <SelectedTagsContainer>
                  {selectedLabels.map((label, index) => (
                    <Tag key={`${label}-${index}`}>
                      <span>{label}</span>
                      <TagRemoveButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveTag(selectedValues[index]);
                        }}
                      >
                        {iconClose}
                      </TagRemoveButton>
                    </Tag>
                  ))}
                </SelectedTagsContainer>
              ) : (
                <strong>{label}</strong>
              )}
            </div>
            <button type="button">{iconArrowTop}</button>
          </SelectHeader>

          <SelectOptionsContainer
            selected={showDropdown}
            itemSelected={false}
            appearWhere={appearWhere}
          >
            <SelectOptionsList>
              {search && (
                <div style={{ padding: "8px" }}>
                  <InputFormField
                    name={`${name}Search`}
                    label={"Busca"}
                    marginBotton="0"
                    icon={true}
                    animation={searching}
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
              )}
              {filteredAndSortedOptions.length > 0 ? (
                filteredAndSortedOptions.map((option) => (
                  <div key={option.value} style={{ padding: "8px 16px" }}>
                    <CheckBox
                      name={`${name}-${option.value}`}
                      label={option.label}
                      value={option.value}
                      checked={selectedValues.includes(option.value)}
                      onChange={handleOptionSelected}
                    />
                  </div>
                ))
              ) : (
                <div style={{ padding: "16px", textAlign: "center", color: "#999" }}>
                  Nenhum resultado encontrado
                </div>
              )}
            </SelectOptionsList>
          </SelectOptionsContainer>
          {error ? <ErrorSpan>{errorMessage}</ErrorSpan> : null}
        </Select>
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
